import { Stack, StackProps, Typography } from '@mui/material'
import { FC } from 'react'
import { useOrdering, useCurrentStore } from 'hooks'
import { useTheme } from '@mui/material/styles'

interface ItemLineProps {
  name?: string
  amount?: number
  variant?: 'total' | 'subtotal'
}

const ItemLine = ({ name, amount, variant = 'subtotal' }: ItemLineProps) => {
  const theme = useTheme()
  const { formatCurrency } = useCurrentStore()
  const textVariant = variant === 'subtotal' ? 'body2' : 'h4'

  return (
    <Stack
      sx={{
        maxWidth: '100%',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(1),
      }}
    >
      <Typography
        variant={textVariant}
        sx={{
          maxWidth: '100%',
          flexGrow: 1,
          flexBasis: 0,
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        }}
        color="secondary"
      >
        {name}
      </Typography>
      <Typography variant={textVariant} textAlign="right" color="secondary">
        {formatCurrency(amount ?? 0)}
      </Typography>
    </Stack>
  )
}

interface OrderSummaryProps extends StackProps {
  showTip: boolean
}

export const OrderSummary: FC<OrderSummaryProps> = ({ showTip, ...props }) => {
  const { order } = useOrdering()

  if (!order) return null

  const discountAmount = order.discount_amount ?? 0
  const discountName = order.discount?.name ?? ''
  const taxPercent = order.tax?.percent ?? 0
  const taxName = order.tax?.name ?? ''
  const customFeeName = order.custom_fee?.name ?? ''
  const customFeeAmount = order.custom_fee?.amount ?? 0
  const customFeePercentName = order.custom_fee_percent?.name ?? ''
  const customFeePercent = order.custom_fee_percent?.percent ?? 0
  const deliveryFee = order.delivery_fee_amount ?? 0
  const tip = order.tip ?? 0
  const total = order.total ?? 0
  const isEstimatedDelivery = order.quote_id && order.quote_id.length > 0
  const isSplitTip =
    order.order_type === 'delivery' &&
    order.tip_for_driver &&
    order.tip_for_restaurant_staff

  return (
    <Stack {...props} spacing={0.5}>
      <ItemLine name="Subtotal" amount={order.subtotal} />
      {discountAmount > 0 && (
        <ItemLine name={discountName} amount={-discountAmount} />
      )}
      {deliveryFee > 0 && (
        <ItemLine
          name={
            isEstimatedDelivery ? 'Delivery fee (estimated)' : 'Delivery fee'
          }
          amount={deliveryFee}
        />
      )}
      {customFeeAmount > 0 && (
        <ItemLine name={customFeeName} amount={customFeeAmount} />
      )}
      {customFeePercent > 0 && (
        <ItemLine
          name={`${customFeePercentName} (${(customFeePercent * 100).toFixed(
            3,
          )}%)`}
          amount={order.subtotal * customFeePercent}
        />
      )}
      {taxPercent > 0 && (
        <ItemLine
          name={`${taxName} (${(taxPercent * 100).toFixed(3)}%)`}
          amount={order.tax_amount}
        />
      )}
      {showTip &&
        tip > 0 &&
        (isSplitTip ? (
          <>
            <ItemLine name="Tip for driver" amount={order.tip_for_driver} />
            <ItemLine
              name="Tip for staff"
              amount={order.tip_for_restaurant_staff}
            />
          </>
        ) : (
          <ItemLine name="Tip" amount={tip} />
        ))}
      <ItemLine
        name="TOTAL"
        amount={showTip ? total : total - tip}
        variant="total"
      />
    </Stack>
  )
}
